import type { AnimationObject } from 'lottie-react-native';

export const MAX_DOTLOTTIE_COMPRESSED_BYTES = 1024 * 1024;
export const MAX_DOTLOTTIE_EXPANDED_BYTES = 8 * 1024 * 1024;
export const MAX_DOTLOTTIE_ENTRIES = 64;
export const MAX_PREPARED_ANIMATION_BYTES = 2 * 1024 * 1024;
export const MAX_SELECTED_ANIMATIONS = 1;

export const SUPPORTED_DOTLOTTIE_IMAGE_MEDIA = ['image/png', 'image/webp', 'image/jpeg'] as const;

export type SupportedDotLottieImageMedia = typeof SUPPORTED_DOTLOTTIE_IMAGE_MEDIA[number];

export type DotLottieFailureStage =
  | 'identity'
  | 'network'
  | 'response-size'
  | 'zip-structure'
  | 'entry-count'
  | 'expanded-size'
  | 'manifest'
  | 'animation-json'
  | 'image-resolution'
  | 'media-detection'
  | 'prepared-size'
  | 'cancellation'
  | 'renderer';

export type LottiePreparationMode = 'uri' | 'prepared' | 'cache' | 'in-flight';

export interface DotLottieManifestAnimation {
  id: string;
}

export interface DotLottieManifest {
  animations: Array<DotLottieManifestAnimation | string>;
  [key: string]: unknown;
}

export interface LottieAnimationAsset {
  id?: string;
  p?: string;
  u?: string;
  [key: string]: unknown;
}

export interface PreparedLottieAnimationSource extends AnimationObject {
  assets: LottieAnimationAsset[];
  [key: string]: unknown;
}

export interface DotLottiePreparationMetadata {
  assetIdentifier: string;
  compressedBytes: number;
  expandedBytes: number;
  preparedBytes: number;
  entryCount: number;
  selectedAnimationId: string;
  embeddedImageCount: number;
  unresolvedImageCount: number;
  durationMs: number;
  preparationMode: LottiePreparationMode;
}

export interface PreparedDotLottieResult {
  source: PreparedLottieAnimationSource;
  metadata: DotLottiePreparationMetadata;
}

export class DotLottiePreparationError extends Error {
  readonly stage: DotLottieFailureStage;
  readonly retryable: boolean;
  readonly assetIdentifier?: string;

  constructor({
    stage,
    message,
    retryable = false,
    assetIdentifier,
  }: {
    stage: DotLottieFailureStage;
    message: string;
    retryable?: boolean;
    assetIdentifier?: string;
  }) {
    super(message);
    this.name = 'DotLottiePreparationError';
    this.stage = stage;
    this.retryable = retryable;
    this.assetIdentifier = assetIdentifier;
  }
}
